import { ECONOMIC_SOURCE_CATALOG } from '../../data/economicContribution/sourceCatalog';
import { APPROVED_COMPARABLE_PROJECTS } from '../../data/economicContribution/seedComparables';
import {
  ECONOMIC_CONTRIBUTION_SCHEMA_VERSION,
  type EconomicContributionScreenRequest,
  type EconomicContributionScreeningSnapshot,
  type EconomicProfileSnapshot,
  type EconomicSourceCatalogSnapshot,
} from '../../types/economicContribution';
import type { TmyDataset } from '../../types/tmy';
import { calculateConstructionJobs, calculateConstructionJobsMwRange } from './constructionJobsModel';
import { calculateDirectJobsMwRangeScreening, calculateDirectJobsScreening } from './directJobsModel';
import { calculateFiscalScenario } from './fiscalScenario';
import { aggregateComparableMatches, matchComparableProjects } from './comparableMatcher';
import { hashEconomicModelInput } from './modelUtils';
import { calculateWaterUseScreening } from './waterUseScreening';

export interface EconomicScreeningDependencies {
  catalog?: EconomicSourceCatalogSnapshot;
  economicProfile?: EconomicProfileSnapshot | null;
  comparables?: typeof APPROVED_COMPARABLE_PROJECTS;
  /** Site-coordinate-selected hourly climate used only by the adiabatic water screen. */
  tmy?: TmyDataset | null;
  /** True only when the server attempted and failed to load the site-coordinate TMY. */
  tmyUnavailable?: boolean;
  now?: Date;
}

export function calculateEconomicContributionScreening(
  request: EconomicContributionScreenRequest,
  dependencies: EconomicScreeningDependencies = {},
): EconomicContributionScreeningSnapshot {
  const catalog = dependencies.catalog ?? ECONOMIC_SOURCE_CATALOG;
  const now = dependencies.now ?? new Date();
  const directJobs = calculateDirectJobsScreening(request.directJobs, catalog, now);
  const directJobsMwRange = request.directJobsLoadRange
    ? calculateDirectJobsMwRangeScreening(request.directJobs, request.directJobsLoadRange, catalog, now)
    : null;
  const constructionJobs = request.constructionJobs
    ? calculateConstructionJobs(request.constructionJobs, catalog, now)
    : null;
  const constructionJobsMwRange = request.constructionJobs && request.directJobsLoadRange
    ? calculateConstructionJobsMwRange(request.constructionJobs, request.directJobsLoadRange, catalog, now)
    : null;
  const waterUseScreening = request.waterUse
    ? calculateWaterUseScreening(request.waterUse, undefined, {
        tmy: dependencies.tmy,
        tmyUnavailable: dependencies.tmyUnavailable,
      })
    : null;
  const matches = matchComparableProjects({
    load: request.directJobs.load,
    operatingModel: request.directJobs.operatingModel,
    workload: request.directJobs.workload,
    stateFips: request.directJobs.location.stateFips,
  }, dependencies.comparables ?? APPROVED_COMPARABLE_PROJECTS);
  const fiscalScenarios = (request.fiscalScenarios ?? []).map(calculateFiscalScenario);
  // Reuse the already loaded public context, comparables and hourly weather.
  // Additional cases contain no nested scenarios and never re-fetch providers.
  const additionalScenarios = request.additionalScenarios?.map(({ scenarioId, label, assumption, ...input }) => {
    const result = calculateEconomicContributionScreening({
      ...input,
      directJobs: { ...input.directJobs, location: request.directJobs.location },
    }, { ...dependencies, catalog, now });
    return {
      scenarioId, label, assumption,
      directJobs: result.directJobs,
      directJobsMwRange: result.directJobsMwRange,
      constructionJobs: result.constructionJobs,
      constructionJobsMwRange: result.constructionJobsMwRange,
      waterUseScreening: result.waterUseScreening,
    };
  });
  const inputHash = hashEconomicModelInput(request);
  const comparableAggregates = aggregateComparableMatches(matches);
  const snapshotContent = {
    siteId: request.directJobs.siteId,
    location: request.directJobs.location,
    inputHash,
    sourceCatalogVersion: catalog.catalogVersion,
    directJobs,
    directJobsMwRange,
    constructionJobs,
    constructionJobsMwRange,
    waterUseScreening,
    economicProfile: dependencies.economicProfile ?? null,
    comparableMatches: matches,
    comparableAggregates,
    fiscalScenarios,
    ...(additionalScenarios?.length ? { additionalScenarios } : {}),
  };
  return {
    schemaVersion: ECONOMIC_CONTRIBUTION_SCHEMA_VERSION,
    snapshotId: `economic-screen-${hashEconomicModelInput(snapshotContent).slice(0, 20)}`,
    ...snapshotContent,
    generatedAt: now.toISOString(),
  };
}
